"use client";

import { useId, useState } from "react";
import { CalendarDays, FileText, Info, UserPlus, Users } from "lucide-react";
import { StatusBadge, Tab, TabList } from "@nitro/ui";
import { CompetitionRegistrationButton } from "./CompetitionRegistrationModal";
import { getRegistrationPresentation } from "../lib/competitionRegistration";

const DEFAULT_PARTICIPATION_NOTICE = "Participación por invitación. No hay inscripción pública directa.";

const tabs = [
  { id: "resumen", label: "Resumen", icon: Info },
  { id: "calendario", label: "Calendario", icon: CalendarDays },
  { id: "inscripcion", label: "Inscripción", icon: UserPlus },
  { id: "reglamento", label: "Reglamento", icon: FileText },
];

export function ChampionshipDetailTabs({ competition }) {
  const baseId = useId();
  const [activeTab, setActiveTab] = useState("resumen");
  const simulatorName = competition.simulator?.name || "Simulador Nitro";
  const legacyHtml = competition.metadata?.legacyHtml || {};
  const officialRulesHtml = sanitizeLegacyHtml(legacyHtml.rules || "");
  const rules = competition.rulesSummary || "";
  const summary = buildCompetitionSummary(competition, simulatorName);
  const registrationPresentation = getRegistrationPresentation(competition);
  const resolvedTabs = tabs.map((tab) => (
    tab.id === "inscripcion" && registrationPresentation.managedParticipation
      ? { ...tab, label: "Participantes", icon: Users }
      : tab
  ));

  return (
    <section className="championship-tabs">
      <TabList
        className="championship-tab-list"
        ariaLabel="Secciones del campeonato"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        {resolvedTabs.map((tab) => {
          const selected = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <Tab
              controls={`${baseId}-${tab.id}-panel`}
              className={selected ? "is-active" : ""}
              id={`${baseId}-${tab.id}-tab`}
              key={tab.id}
              value={tab.id}
              icon={<Icon size={16} />}
            >
              {tab.label}
            </Tab>
          );
        })}
      </TabList>

      <div className="championship-tab-shell">
        <div
          aria-labelledby={`${baseId}-${activeTab}-tab`}
          className="championship-tab-panel championship-panel"
          id={`${baseId}-${activeTab}-panel`}
          role="tabpanel"
        >
          {activeTab === "resumen" ? <SummaryPanel summary={summary} /> : null}
          {activeTab === "calendario" ? <CalendarPanel competition={competition} /> : null}
          {activeTab === "inscripcion" ? (
            <RegistrationPanel competition={competition} simulatorName={simulatorName} summary={summary} />
          ) : null}
          {activeTab === "reglamento" ? <RulesPanel officialRulesHtml={officialRulesHtml} rules={rules} /> : null}
        </div>
      </div>
    </section>
  );
}

function SummaryPanel({ summary }) {
  const descriptionHtml = sanitizePublicHtml(summary.description || summary.rulesSummary || "");
  return (
    <article className="championship-overview-panel">
      <div className="championship-overview-copy">
        <p className="nitro-eyebrow">Resumen del campeonato</p>
        <h2>Qué se corre</h2>
        {descriptionHtml ? (
          <div className="championship-html-content is-summary" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
        ) : (
          <p>Descripción por publicar.</p>
        )}
        <div className="championship-overview-facts">
          <InfoFact label="Simulador" value={summary.simulatorName} />
          <InfoFact label="Comunidad" value={summary.communityName} />
          <InfoFact label="Auto / clase" value={summary.carLabel} />
          <InfoFact label="Pista inicial" value={summary.trackLabel} />
          <InfoFact label="Temporada" value={summary.seasonLabel} />
          <InfoFact label={summary.managedParticipation ? "Participación" : "Inscripción"} value={summary.registrationLabel} />
          <InfoFact
            label={summary.managedParticipation ? "Nómina" : "Inscritos"}
            value={summary.managedParticipation && !summary.entryCount ? "Pendiente" : `${summary.entryCount} pilotos`}
          />
          <InfoFact
            label={summary.managedParticipation ? "Representantes previstos" : "Cupos por fecha"}
            value={summary.managedParticipation
              ? summary.maxEntries ? `${summary.maxEntries} pilotos` : "Por definir"
              : summary.roundCapacity ? `${summary.roundCapacity} pilotos` : "Por definir"}
          />
        </div>
      </div>

      <div className="championship-overview-rules">
        <OverviewRule title="Elegibilidad" items={summary.eligibilityItems} />
        <OverviewRule title="Formato de carrera" items={summary.raceFormatItems} />
      </div>

      {summary.driverCategories.length ? (
        <div className="championship-overview-categories">
          {summary.driverCategories.map((categoryItem) => (
            <article key={categoryItem.id || categoryItem.slug} style={categoryItem.colorHex ? { "--category-color": categoryItem.colorHex } : undefined}>
              <span>{categoryItem.name}</span>
              <strong>{categoryRuleLabel(categoryItem)}</strong>
              <small>{categoryNumberLabel(categoryItem)}</small>
            </article>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function ChampionshipSummarySidebar({ competition }) {
  const simulatorName = competition.simulator?.name || "Simulador Nitro";
  const summary = buildCompetitionSummary(competition, simulatorName);
  return (
    <aside className="championship-summary-sidebar" aria-label="Resumen rapido del campeonato">
      {summary.posterImage ? <img src={summary.posterImage} alt="" /> : null}
      {summary.managedParticipation ? (
        <div className="championship-registration-empty">
          <strong>Participación por invitación</strong>
          <p>{summary.participationNotice}</p>
        </div>
      ) : <CompetitionRegistrationButton className="nitro-primary-button" competition={competition} />}
      <div className="championship-summary-count">
        <span>Formato</span>
        <strong>{summary.raceOrRoundCount}</strong>
        <small>{summary.raceCount ? "carreras programadas" : "rondas publicadas"}</small>
      </div>
      <dl>
        <div>
          <dt>{summary.managedParticipation ? "Nómina" : "Inscritos"}</dt>
          <dd>{summary.managedParticipation && !summary.entryCount ? "Pendiente" : summary.entryCount}</dd>
        </div>
        <div>
          <dt>Categorías</dt>
          <dd>{summary.categoryCount || "Por definir"}</dd>
        </div>
        <div>
          <dt>Check-in</dt>
          <dd>{competition.usesCheckIn ? "Activo" : "No activo"}</dd>
        </div>
        <div>
          <dt>Splits</dt>
          <dd>{competition.usesSplits ? "Múltiples" : "División única"}</dd>
        </div>
        <div>
          <dt>{summary.managedParticipation ? "Participación" : "Inscripción"}</dt>
          <dd>{summary.registrationLabel}</dd>
        </div>
      </dl>
    </aside>
  );
}

export function ChampionshipMobileRegistrationCard({ competition }) {
  const simulatorName = competition.simulator?.name || "Simulador Nitro";
  const summary = buildCompetitionSummary(competition, simulatorName);
  return (
    <aside
      className="championship-mobile-registration"
      aria-label={summary.managedParticipation ? "Participación en el campeonato" : "Inscripción al campeonato"}
    >
      {summary.posterImage ? <img src={summary.posterImage} alt="" /> : null}
      <div>
        <span>{summary.managedParticipation ? "Participación" : `${summary.entryCount} inscritos`}</span>
        <strong>{summary.registrationLabel}</strong>
        <small>{summary.managedParticipation
          ? summary.entryCount ? `${summary.entryCount} participantes publicados` : "Nómina pendiente"
          : summary.maxEntries ? `${summary.maxEntries} cupos` : summary.roundCapacity ? `${summary.roundCapacity} por fecha` : `${summary.raceOrRoundCount} fechas`}</small>
      </div>
      {summary.managedParticipation ? null : <CompetitionRegistrationButton className="nitro-primary-button" competition={competition} />}
    </aside>
  );
}

function buildCompetitionSummary(competition, simulatorName) {
  const category = competition.categories?.[0] || null;
  const nextRound = nextScheduledRound(competition.rounds);
  const raceSession = [...(competition.sessions || [])].find((session) => session.isRace) || competition.sessions?.[0] || null;
  const track = raceSession?.track || nextRound?.track || null;
  const driverCategories = getActiveCategories(competition, "driver_class");
  const carCategories = getActiveCategories(competition, "car_class");
  const raceSessions = (competition.sessions || []).filter((session) => session.isRace || ["race", "race_2"].includes(session.sessionType));
  const metadata = competition.metadata || {};
  const registration = metadata.registration || {};
  const eligibility = metadata.iracingEligibility || {};
  const raceFormat = metadata.raceFormat || {};
  const prequaly = metadata.prequaly || {};
  const description = competition.description || "";
  const registrationPresentation = getRegistrationPresentation(competition);
  const participationNotice = publicParticipationNotice(competition);
  const carLabel = carCategories.length
    ? carCategories.map(categoryLabel).join(", ")
    : category?.carClass?.name || category?.name || "Categoría por definir";
  const communityName = competition.organization?.shortName || competition.organization?.name || "Nitro Simracing";
  const categoryCount = driverCategories.length + carCategories.length;
  const maxEntries = competition.maxEntries || null;
  const roundCapacity = registration.maxDriversPerRound || prequaly.maxQualifiedPerRound || null;
  const raceCount = raceSessions.length;
  return {
    carLabel,
    categoryCount,
    communityName,
    description,
    driverCategories,
    eligibilityItems: buildEligibilityItems(competition, eligibility, registration),
    entryCount: getCompetitionEntryCount(competition),
    maxEntries,
    managedParticipation: registrationPresentation.managedParticipation,
    participationNotice,
    posterImage: competition.thumbnailUrl || competition.bannerUrl || "",
    raceCount,
    raceFormatItems: buildRaceFormatItems(raceFormat, prequaly),
    raceOrRoundCount: raceCount || competition.rounds?.length || 0,
    registrationLabel: registrationModeLabel(competition.registrationMode),
    roundCapacity,
    rulesSummary: competition.rulesSummary,
    seasonLabel: formatDateRange(competition.startsAt, competition.endsAt),
    simulatorName,
    trackLabel: formatTrack(track),
  };
}

function getCompetitionEntryCount(competition) {
  return (competition.entries || []).filter((entry) => !["cancelled", "rejected", "withdrawn"].includes(entry.status)).length;
}

function getPublicEntries(competition) {
  return (competition.entries || [])
    .filter((entry) => !["cancelled", "rejected", "withdrawn"].includes(entry.status))
    .sort((a, b) => {
      const statusOrder = { approved: 1, pending: 2, waitlist: 3 };
      return Number(a.driverClass?.sortOrder || 100) - Number(b.driverClass?.sortOrder || 100)
        || Number(b.rating || 0) - Number(a.rating || 0)
        || (statusOrder[a.status] || 9) - (statusOrder[b.status] || 9)
        || new Date(a.registeredAt || 0).getTime() - new Date(b.registeredAt || 0).getTime()
        || Number(a.id || 0) - Number(b.id || 0);
    });
}

function CalendarPanel({ competition }) {
  const rounds = buildCalendarTree(competition);
  const totals = {
    rounds: rounds.length,
    meetings: rounds.reduce((sum, round) => sum + round.meetings.length, 0),
    sessions: rounds.reduce((sum, round) => sum + round.meetings.reduce((inner, meeting) => inner + meeting.sessions.length, 0), 0),
  };

  return (
    <>
      <div className="championship-panel-head">
        <div>
          <p className="nitro-eyebrow">Calendario</p>
          <h2>Agenda por rondas</h2>
          <p>Rondas, jornadas y sesiones del campeonato en horario America/Bogota.</p>
        </div>
        <span>{totals.rounds} rondas / {totals.meetings} jornadas / {totals.sessions} sesiones</span>
      </div>
      <div className="championship-calendar-tree">
        {rounds.length ? rounds.map((round) => (
          <article className="championship-calendar-round" key={round.id}>
            <header
              className={`championship-calendar-round__head${round.track?.thumbnailUrl ? " has-track-image" : ""}`}
              style={round.track?.thumbnailUrl ? { "--round-track-image": `url("${round.track.thumbnailUrl}")` } : undefined}
            >
              <div>
                <span>Ronda {round.roundNumber}</span>
                <strong>{round.name}</strong>
                {round.metadata?.divisionName ? <p>{round.metadata.divisionName}</p> : null}
              </div>
              <StatusPill status={round.status} />
            </header>

            <div className="championship-calendar-meetings">
              {round.meetings.length ? round.meetings.map((meeting) => (
                <section className="championship-calendar-meeting" key={meeting.id}>
                  <div className="championship-calendar-meeting__body">
                    <div className="championship-calendar-meeting__top">
                      <div>
                        <span>Jornada {meeting.meetingNumber}</span>
                        <strong>{meeting.name}</strong>
                        <p>{formatTrack(meeting.track)}</p>
                      </div>
                      <div className="championship-calendar-meeting__date">
                        <strong>{formatBogotaParts(meeting.startsAt).dateLabel}</strong>
                        <span>{formatBogotaParts(meeting.startsAt).timeLabel}</span>
                      </div>
                    </div>

                    <div className="championship-calendar-sessions">
                      {meeting.sessions.length ? meeting.sessions.map((session) => (
                        <article className={`championship-calendar-session is-${session.sessionType}`} key={session.id}>
                          <div>
                            <span>{sessionTypeLabel(session.sessionType)}</span>
                            <strong>{session.name}</strong>
                            <small>{session.config?.sigla || session.sessionType}</small>
                          </div>
                          <div className="championship-calendar-session__time">
                            <strong>{formatBogotaParts(session.startsAt).dateLabel}</strong>
                            <span>{formatSessionRange(session)}</span>
                          </div>
                          <div className="championship-calendar-session__badges">
                            <StatusPill status={session.status} />
                            {session.isPointsScoring ? <span>Puntuable</span> : null}
                            {session.config?.isClassifying ? <span>Clasificatoria</span> : null}
                            {session.config?.isRequired ? <span>Obligatoria</span> : null}
                          </div>
                        </article>
                      )) : (
                        <p className="championship-calendar-empty">Sin sesiones publicadas para esta jornada.</p>
                      )}
                    </div>
                  </div>
                </section>
              )) : (
                <p className="championship-calendar-empty">Sin jornadas publicadas para esta ronda.</p>
              )}
            </div>
          </article>
        )) : (
          <p>El calendario se publicará próximamente.</p>
        )}
      </div>
    </>
  );
}

function RegistrationPanel({ competition, simulatorName, summary }) {
  const entries = getPublicEntries(competition);
  const driverCategories = getActiveCategories(competition, "driver_class");
  const carCategories = getActiveCategories(competition, "car_class");

  return (
    <div className="championship-registration-panel">
      <div>
        <p className="nitro-eyebrow">{summary?.managedParticipation ? "Participación" : "Inscripción"}</p>
        <h2>{summary?.managedParticipation ? "Participantes" : "Inscritos"}</h2>
        <p>{summary?.managedParticipation
          ? summary.participationNotice
          : entries.length ? `${entries.length} pilotos registrados para este campeonato.` : "Aún no hay pilotos inscritos publicados."}</p>
      </div>

      <div className="championship-entry-summary">
        <InfoFact label="Simulador" value={simulatorName} />
        <InfoFact
          label={summary?.managedParticipation ? "Participantes publicados" : "Inscritos"}
          value={summary?.managedParticipation && !entries.length ? "Pendiente" : `${entries.length} pilotos`}
        />
        {summary?.managedParticipation ? (
          <InfoFact label="Representantes previstos" value={summary.maxEntries ? `${summary.maxEntries} pilotos` : "Por definir"} />
        ) : (
          <>
            <InfoFact label="Cupos inscripción" value={summary?.maxEntries ? `${entries.length}/${summary.maxEntries}` : "Sin límite global"} />
            <InfoFact label="Cupos por fecha" value={summary?.roundCapacity ? `${summary.roundCapacity} pilotos` : "Por definir"} />
          </>
        )}
        <InfoFact label="Categorías piloto" value={driverCategories.length ? driverCategories.map((category) => category.name).join(", ") : "No aplica"} />
        <InfoFact label="Categorías auto" value={carCategories.length ? carCategories.map(categoryLabel).join(", ") : summary?.carLabel} />
      </div>

      {entries.length ? (
        <div className="championship-entries-table-wrap" role="region" aria-label="Participantes publicados" tabIndex={0}>
          <table className="championship-entries-table">
            <thead>
              <tr>
                <th>Piloto</th>
                <th>Categoría</th>
                <th>Auto / clase</th>
                <th>Número</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <strong>{entryDisplayName(entry)}</strong>
                    <span>{entry.rating ? `iRating ${Math.trunc(entry.rating)}` : "iRating por confirmar"}</span>
                  </td>
                  <td><EntryCategoryPill entry={entry} fallback={driverCategories.length ? "Por asignar" : "No aplica"} /></td>
                  <td><EntryCarLabel entry={entry} carCategories={carCategories} /></td>
                  <td>{entry.raceNumber ? `#${entry.raceNumber}` : "-"}</td>
                  <td><StatusPill status={entry.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="championship-registration-empty">
          <strong>{summary?.managedParticipation ? "Nómina pendiente de publicación" : "Primeros cupos disponibles"}</strong>
          <p>{summary?.managedParticipation
            ? summary.participationNotice
            : "El botón de inscripción está debajo de la imagen del campeonato para que el piloto pueda entrar directo."}</p>
        </div>
      )}
    </div>
  );
}

function RulesPanel({ officialRulesHtml, rules }) {
  const rulesHtml = sanitizePublicHtml(rules);
  return (
    <>
      <p className="nitro-eyebrow">Reglamento</p>
      <h2>Reglas principales</h2>
      {officialRulesHtml ? (
        <div className="championship-html-content" dangerouslySetInnerHTML={{ __html: officialRulesHtml }} />
      ) : rulesHtml ? (
        <div className="championship-html-content" dangerouslySetInnerHTML={{ __html: rulesHtml }} />
      ) : (
        <p>Reglamento por publicar. Esta pestaña no contiene reglas oficiales todavía.</p>
      )}
    </>
  );
}

function InfoFact({ label, value }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value || "Por definir"}</strong>
    </div>
  );
}

function OverviewRule({ items, title }) {
  if (!items.length) return null;
  return (
    <section>
      <h3>{title}</h3>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </section>
  );
}

function buildEligibilityItems(competition, eligibility, registration) {
  const minimumLicense = registration.minimumLicense || eligibility.licenseMinimum;
  return [
    minimumLicense ? `Licencia mínima ${minimumLicense}.` : null,
    eligibility.threshold ? `Categoría por iRating histórico: PRO sobre ${eligibility.threshold}, AM hasta ${eligibility.threshold}.` : null,
    eligibility.lookbackMonths ? `Se revisan los últimos ${eligibility.lookbackMonths} meses al inscribirse.` : null,
    competition.usesDriverClasses ? "La categoría del piloto queda vinculada a la inscripción." : null,
  ].filter(Boolean);
}

function buildRaceFormatItems(raceFormat, prequaly) {
  return [
    prequaly.requiredPerRound ? `Preclasificación por ronda${prequaly.sessionsPerRound ? ` con ${prequaly.sessionsPerRound} sesiones` : ""}.` : null,
    raceFormat.targetDurationMinutes ? `Carrera objetivo de ${raceFormat.targetDurationMinutes} minutos.` : null,
    raceFormat.qualifyingMinutes ? `Clasificación de ${raceFormat.qualifyingMinutes} minutos.` : null,
    raceFormat.fuelTankLimitPercent ? `Tanque limitado al ${raceFormat.fuelTankLimitPercent}%.` : null,
  ].filter(Boolean);
}

function getActiveCategories(competition, type) {
  return (competition.categories || [])
    .filter((category) => category.status !== "inactive" && (!type || category.categoryType === type))
    .sort((a, b) => Number(a.sortOrder || 100) - Number(b.sortOrder || 100));
}

function categoryLabel(category) {
  return category.name || category.carClass?.shortName || category.carClass?.name;
}

function categoryRuleLabel(category) {
  if (category.minRating && category.maxRating) return `Rating ${category.minRating}-${category.maxRating}`;
  if (category.minRating) return `Rating mayor o igual a ${category.minRating}`;
  if (category.maxRating) return `Rating hasta ${category.maxRating}`;
  return category.separateStandings ? "Tabla independiente" : "Categoría activa";
}

function categoryNumberLabel(category) {
  if (category.numberMin && category.numberMax) return `Números ${category.numberMin}-${category.numberMax}`;
  if (category.capacity) return `${category.capacity} cupos`;
  return "Configuración abierta";
}

function entryDisplayName(entry) {
  return entry.pilot?.displayName || entry.teamName || "Piloto por confirmar";
}

function entryCarLabel(entry, carCategories = []) {
  const carName = entry.car?.name || "";
  const className = readableCarClassName(entry.carClass?.name || "");
  if (carName && className) return `${carName} / ${className}`;
  if (carName) return carName;
  if (className) return className;
  return carCategories.length ? "Por asignar" : "No aplica";
}

function EntryCategoryPill({ entry, fallback }) {
  const color = entry.driverClass?.colorHex;
  return (
    <span className="championship-entry-category" style={color ? { "--entry-category-color": color } : undefined}>
      {entry.driverClass?.name || fallback}
    </span>
  );
}

function EntryCarLabel({ carCategories, entry }) {
  const image = entry.car?.thumbnailUrl || entry.car?.imageUrl || "";
  return (
    <span className="championship-entry-car">
      {image ? <img src={image} alt="" /> : null}
      <span>{entryCarLabel(entry, carCategories)}</span>
    </span>
  );
}

function readableCarClassName(value) {
  if (!value || value === "sports_car") return "";
  return value;
}

function registrationModeLabel(mode) {
  const map = {
    open: "Abierta",
    approval_required: "Con aprobación",
    invite_only: "Por invitación",
    closed: "Cerrada",
  };
  return map[mode] || "Abierta";
}

function publicParticipationNotice(competition) {
  const value = competition?.metadata?.registration?.participationNotice;
  return typeof value === "string" && value.trim() ? value.trim() : DEFAULT_PARTICIPATION_NOTICE;
}

function nextScheduledRound(rounds = []) {
  return [...rounds].find((round) => ["scheduled", "in_progress"].includes(round.status)) || rounds[0] || null;
}

function formatTrack(track) {
  if (!track) return "Pista por definir";
  return [track.name, track.variantName].filter(Boolean).join(" - ");
}

function formatDateRange(startValue, endValue) {
  const start = formatCompactDate(startValue);
  const end = formatCompactDate(endValue);
  if (start && end) return `${start} - ${end}`;
  return start || end || "Por confirmar";
}

function formatCompactDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "short",
    timeZone: "America/Bogota",
    year: "numeric",
  }).format(date);
}

function formatBogotaParts(value) {
  if (!value) return { dateLabel: "Fecha por confirmar", timeLabel: "Hora por confirmar" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { dateLabel: "Fecha por confirmar", timeLabel: "Hora por confirmar" };
  return {
    dateLabel: new Intl.DateTimeFormat("es-CO", {
      day: "2-digit",
      month: "short",
      timeZone: "America/Bogota",
      weekday: "short",
    }).format(date),
    timeLabel: `${new Intl.DateTimeFormat("es-CO", {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
      timeZone: "America/Bogota",
    }).format(date)} H`,
  };
}

function buildCalendarTree(competition) {
  const sessionsByMeeting = new Map();
  for (const session of competition.sessions || []) {
    const key = session.meetingId || `round-${session.roundId || "none"}`;
    if (!sessionsByMeeting.has(key)) sessionsByMeeting.set(key, []);
    sessionsByMeeting.get(key).push(session);
  }

  const meetingsByRound = new Map();
  for (const meeting of competition.meetings || []) {
    const sessions = (sessionsByMeeting.get(meeting.id) || []).sort(sortBySchedule);
    const item = { ...meeting, sessions };
    const key = meeting.roundId || "none";
    if (!meetingsByRound.has(key)) meetingsByRound.set(key, []);
    meetingsByRound.get(key).push(item);
  }

  for (const session of competition.sessions || []) {
    if (session.meetingId) continue;
    const roundKey = session.roundId || "none";
    if (!meetingsByRound.has(roundKey)) meetingsByRound.set(roundKey, []);
    const fallbackId = `fallback-${roundKey}`;
    const fallbackMeeting = meetingsByRound.get(roundKey).find((meeting) => meeting.id === fallbackId);
    if (fallbackMeeting) {
      fallbackMeeting.sessions.push(session);
    } else {
      meetingsByRound.get(roundKey).push({
        id: fallbackId,
        roundId: session.roundId,
        meetingNumber: 1,
        name: session.roundName || "Jornada",
        status: session.status,
        startsAt: session.startsAt,
        endsAt: session.endsAt,
        track: session.track,
        sessions: [session],
        metadata: {},
      });
    }
  }

  const rounds = (competition.rounds || []).map((round) => ({
    ...round,
    meetings: (meetingsByRound.get(round.id) || []).sort(sortBySchedule).map((meeting) => ({
      ...meeting,
      sessions: [...(meeting.sessions || [])].sort(sortBySchedule),
    })),
  }));

  if (meetingsByRound.has("none")) {
    rounds.push({
      id: "none",
      roundNumber: rounds.length + 1,
      name: "Sin ronda asignada",
      status: "scheduled",
      startsAt: null,
      metadata: {},
      meetings: meetingsByRound.get("none").sort(sortBySchedule),
    });
  }

  return rounds.sort(sortBySchedule);
}

function sortBySchedule(a, b) {
  const aOrder = Number(a.sortOrder || 0);
  const bOrder = Number(b.sortOrder || 0);
  if (aOrder !== bOrder) return aOrder - bOrder;
  return new Date(a.startsAt || 0).getTime() - new Date(b.startsAt || 0).getTime();
}

function StatusPill({ status }) {
  return <StatusBadge tone={statusTone(status)}>{statusLabel(status)}</StatusBadge>;
}

function statusTone(status) {
  if (["registration_open", "approved", "finished", "server_ready"].includes(status)) return "success";
  if (["pending", "waitlist"].includes(status)) return "warning";
  if (status === "cancelled") return "danger";
  if (["in_progress", "check_in_open"].includes(status)) return "live";
  if (["scheduled", "splitting"].includes(status)) return "info";
  return "neutral";
}

function statusLabel(status) {
  const map = {
    draft: "Borrador",
    scheduled: "Programada",
    registration_open: "Inscripción abierta",
    approved: "Aprobado",
    pending: "Pendiente",
    waitlist: "Espera",
    in_progress: "En curso",
    finished: "Finalizada",
    cancelled: "Cancelada",
    check_in_open: "Check-in abierto",
    check_in_closed: "Check-in cerrado",
    splitting: "Armando splits",
    server_ready: "Servidor listo",
  };
  return map[status] || "Programada";
}

function sessionTypeLabel(type) {
  const map = {
    practice: "Práctica",
    prequaly: "Prequaly",
    qualifying: "Clasificación",
    race: "Carrera",
    race_2: "Carrera 2",
    hotlap: "Hotlap",
    rally_stage: "Etapa",
    other: "Sesión",
  };
  return map[type] || "Sesión";
}

function formatSessionRange(session) {
  const start = formatBogotaParts(session.startsAt).timeLabel;
  const end = formatBogotaParts(session.endsAt).timeLabel;
  if (start && end && end !== "Hora por confirmar") return `${start} - ${end}`;
  return start;
}

function shortText(value, maxLength) {
  const text = stripHtml(value).replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}...`;
}

function stripHtml(value) {
  return String(value || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function sanitizeLegacyHtml(value) {
  return sanitizePublicHtml(value);
}

function sanitizePublicHtml(value) {
  const allowedTags = new Set(["p", "br", "strong", "b", "em", "i", "ul", "ol", "li", "h2", "h3", "h4", "a", "hr", "table", "thead", "tbody", "tr", "th", "td"]);
  const voidTags = new Set(["br", "hr"]);
  return String(value || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
    .replace(/<[^>]+>/g, (tag) => {
      const match = tag.match(/^<\s*(\/?)\s*([a-z0-9-]+)([^>]*)>/i);
      if (!match) return "";
      const closing = !!match[1];
      const name = match[2].toLowerCase();
      const attrs = match[3] || "";
      if (!allowedTags.has(name)) return "";
      if (voidTags.has(name)) return `<${name}>`;
      if (closing) return `</${name}>`;
      if (name !== "a") return `<${name}>`;
      const href = extractSafeHref(attrs);
      if (!href) return "<a>";
      const className = /\bclass\s*=\s*(['"])(.*?)\1/i.exec(attrs)?.[2] || "";
      const classAttr = className.split(/\s+/).includes("nitro-inline-button") ? ' class="nitro-inline-button"' : "";
      return `<a${classAttr} href="${escapeHtmlAttribute(href)}" target="_blank" rel="noopener noreferrer">`;
    })
    .trim();
}

function extractSafeHref(attrs) {
  const match = /\bhref\s*=\s*(['"])(.*?)\1/i.exec(attrs || "");
  const href = match?.[2]?.trim() || "";
  if (!href) return "";
  if (/^(https?:\/\/|mailto:|\/)/i.test(href)) return href;
  return "";
}

function escapeHtmlAttribute(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
